import { canEditProject, getProjectRoles } from '@/data/entities'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { createFolder } from '@/data/mutations'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import * as toast from '@/components/ui/toast'
import { mutationKeys } from '@/data/mutations'
import * as Sentry from '@sentry/react'
import { queryKeys } from '@/data/queries'
import type { Folder } from '@/data/entities'

export default function NewFolderDialog({
  children,
  projectId,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onFolderCreated,
}: {
  children?: React.ReactNode
  projectId: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onFolderCreated?: (folderName: string) => void
}) {
  const isControlled = controlledOpen !== undefined
  const {
    authState,
    TENANT_ID,
    isOrgAdmin,
    isOrgEditor,
    permissionExpression,
  } = useRouteContext({
    from: '/_authenticated',
  })
  const queryClient = useQueryClient()
  const [folderName, setFolderName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const projectRole = getProjectRoles(
    permissionExpression ?? undefined,
    TENANT_ID!,
  ).get(projectId)
  const canEdit = canEditProject({
    isOrgAdmin: !!isOrgAdmin,
    isOrgEditor: !!isOrgEditor,
    projectRole,
  })

  const createFolderMutation = useMutation({
    mutationKey: mutationKeys.folders.create(TENANT_ID!, projectId),
    mutationFn: async ({ name }: { name: string }) => {
      return await createFolder({
        authState: authState!,
        tenantId: TENANT_ID!,
        projectId,
        name,
      })
    },
    onMutate: async ({ name }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.folders.byProject(TENANT_ID!, projectId),
      })
      const previousFolders = queryClient.getQueryData<Folder[]>(
        queryKeys.folders.byProject(TENANT_ID!, projectId),
      )
      // Optimistically add the new folder
      queryClient.setQueryData<Folder[]>(
        queryKeys.folders.byProject(TENANT_ID!, projectId),
        (old) => [
          ...(old || []),
          {
            id: `temp-${Date.now()}`,
            projectId,
            name,
            creator: authState?.uid || null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ],
      )
      return { previousFolders }
    },
    onSuccess: (_, variables) => {
      if (isControlled) {
        controlledOnOpenChange?.(false)
      } else {
        setIsOpen(false)
      }
      setFolderName('')
      toast.success('Folder created successfully')
      onFolderCreated?.(variables.name)
    },
    onError: (error, __, context) => {
      Sentry.captureException(error)
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create folder'
      toast.error(errorMessage)
      if (context?.previousFolders) {
        queryClient.setQueryData(
          queryKeys.folders.byProject(TENANT_ID!, projectId),
          context.previousFolders,
        )
      }
    },
    onSettled: () => {
      setIsLoading(false)
      queryClient.invalidateQueries({
        queryKey: ['folders'],
      })
    },
  })

  const handleCreate = (name: string) => {
    if (!canEdit) {
      toast.error('You do not have permission to create folders')
      return
    }
    setIsLoading(true)
    createFolderMutation.mutate({ name })
  }

  const onDialogOpenChange = useCallback(
    (open: boolean) => {
      if (isControlled) {
        controlledOnOpenChange?.(open)
      } else {
        setIsOpen(open)
      }
      if (!open) {
        setFolderName('')
      }
    },
    [isControlled, controlledOnOpenChange],
  )

  // Validate folder name according to API rules
  const isValidFolderName = (name: string) => {
    if (name.length === 0 || name.length > 255) return false
    if (name === 'root') return false
    // Pattern: ^[A-Za-z0-9_-]+$ (alphanumeric, underscore, hyphen)
    const pattern = /^[A-Za-z0-9_-]+$/
    return pattern.test(name)
  }

  const isValid = isValidFolderName(folderName)

  const dialogOpen = isControlled ? controlledOpen : isOpen

  return (
    <Dialog open={dialogOpen} onOpenChange={onDialogOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Folder</DialogTitle>
          <DialogDescription>
            Create a new folder to organize documents in this workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="folder-name">Folder Name</Label>
          <Input
            id="folder-name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="e.g. package_machine"
          />
          <p className="text-muted-foreground text-sm">
            Use only letters, numbers, hyphens, and underscores. No spaces.
          </p>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={() => handleCreate(folderName)}
            disabled={isLoading || !isValid || !canEdit}
          >
            {isLoading && <Loader2 className="size-4 animate-spin" />}
            Create Folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
